import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from 'theme/withStyles';
import { Collapse } from 'react-collapse';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import PlusMinus from 'common/PlusMinus';
import homeActions from 'core/home/actions';

import buttonLinkStyles from 'styles/buttonLink';
import commonStyles from './styles';

const styles = (theme) => {
  const common = commonStyles(theme);

  return {
    ...common,
    buttonLink: buttonLinkStyles(theme),
    disabledItem: {
      color: theme.palette.grey[500],
      opacity: 0.5,
      pointerEvents: 'none',
    },
    hidden: {
      display: 'none',
    },
  };
};

const renderSigner = (signer) => (
  <React.Fragment key={signer.id}>
    {signer.signedAt ? (
      <span className="status">{signer.statusStr}</span>
    ) : (
      <span className="status" data-status="pending">
        {signer.fullName}
        {': '}
        <span className="status">{signer.statusStr}</span>
      </span>
    )}
    <br />
  </React.Fragment>
);

export const HomeAgreementsPanelBare = ({ classes, openDialog, request, resources }) => {
  const { data, isOpen } = useSelector(({ home }) => ({
    data:
      (home[request.id] && home[request.id].agreements && home[request.id].agreements.data) || null,
    isOpen:
      (home[request.id] && home[request.id].agreements && home[request.id].agreements.isOpen) ||
      false,
  }));
  const dispatch = useDispatch();

  useEffect(() => {
    if (resources?.eSignAccepted === 'true' && isOpen) {
      dispatch(homeActions.fetch(request.id, 'agreements', { force: true }));
    }
  }, [resources?.eSignAccepted, dispatch, request.id]);

  const hasPendingAgreements = (data && data.filter((a) => a.pendingSignature).length > 0) || !data;

  const headerText = useMemo(
    () => (
      <>
        <RawHtml type="span">{resources.agreementsTitle}</RawHtml>
        {request.pendingAgreements && hasPendingAgreements && (
          <RawHtml
            type="span"
            className="pending-label"
          >{` * ${resources.pendingAgreementLabel}`}</RawHtml>
        )}
      </>
    ),
    [
      hasPendingAgreements,
      request.pendingAgreements,
      resources.agreementsTitle,
      resources.pendingAgreementLabel,
    ],
  );

  const panelBody = useMemo(() => {
    if (data === null || data === undefined) {
      return <Spinner loading />;
    }
    if (data.length === 0) {
      return <p>This application currently has no agreements.</p>;
    }

    return (
      <ul>
        {data.map((d) => (
          <li
            data-name={d.agreementLabel}
            data-id={d.agreementId}
            data-status={d.statusStr}
            className={classnames({ [classes.disabledItem]: d.disabled })}
            key={d.agreementId}
          >
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <button
              type="button"
              disabled={!!d.disabled}
              className={classnames('buttonLink name', classes.buttonLink)}
              onClick={(e) => {
                e.preventDefault();
                const disclosureType =
                  d.pendingSignature && request.pendingAgreements
                    ? 'home_esign'
                    : 'home_agreements';
                openDialog(disclosureType, {
                  agreement: d,
                  request,
                });
              }}
            >
              {d.agreementLabel}
            </button>
            {d.deliveryOnly ? (
              <span className={classnames('status', classes.status)}>Delivery Only</span>
            ) : (
              <div className={classnames('status-wrapper', classes.status)}>
                {d.agreementSigners && d.agreementSigners.map(renderSigner)}
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  }, [classes.buttonLink, classes.status, data, openDialog, request]);

  return (
    <div
      className={classnames('panel panel-primary loading panel-agreements', classes.root)}
      role="tab"
    >
      <div className="panel-heading" id={`collapseHeading-agreements-${request.id}`}>
        <div
          role="button"
          tabIndex="0"
          aria-expanded={isOpen}
          aria-controls={`collapseAreaID-agreements-${request.id}`}
          className={classnames('panel-title', classes.panelTitle)}
          onClick={(e) => {
            e.preventDefault();
            dispatch(homeActions.fetch(request.id, 'agreements'));
          }}
          onKeyUp={(e) =>
            (e.keyCode === 13 || e.keyCode === 32) &&
            dispatch(homeActions.fetch(request.id, 'agreements'))
          }
        >
          <div
            data-toggle="collapse"
            href={`#collapseHeading-agreements-${request.id}`}
            className={classnames('buttonLink get-agreements', classes.buttonLink, {
              collapsed: !isOpen,
              [classes.collapsed]: !isOpen,
            })}
          >
            <PlusMinus collapsed={!isOpen} /> {headerText}
          </div>
        </div>
      </div>
      <Collapse isOpened={isOpen}>
        <div id={`collapseAreaID-agreements-${request.id}`} className="panel-collapse">
          <div
            className={classnames('panel-body', classes.panelBody, {
              [classes.hidden]: !isOpen,
            })}
          >
            {panelBody}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

HomeAgreementsPanelBare.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  openDialog: PropTypes.func.isRequired,
  request: PropTypes.shape({
    id: PropTypes.number,
    pendingAgreements: PropTypes.bool,
  }).isRequired,
  resources: PropTypes.shape({
    agreementsTitle: PropTypes.string,
    eSignAccepted: PropTypes.string,
    pendingAgreementLabel: PropTypes.string,
  }).isRequired,
};

const HomeAgreementsPanelStyled = withStyles(styles)(HomeAgreementsPanelBare);

export default HomeAgreementsPanelStyled;
